import React, { useState, useEffect, Fragment } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "../../../api";
import swal from "sweetalert";
import { Row, Col, Card, Button, Form, Spinner } from "react-bootstrap";
import PageTitle from "../../layouts/PageTitle";

const OfficeExpenseForm = () => {
 const navigate = useNavigate();
 const { id } = useParams();
 const isEdit = !!id;

 const [offices, setOffices] = useState([]);
 const [expenseTypes, setExpenseTypes] = useState([]);
 const [vendors, setVendors] = useState([]);
 const [loading, setLoading] = useState(false);
 const [submitting, setSubmitting] = useState(false);
 const [selectedFiles, setSelectedFiles] = useState([]);
 const [existingBills, setExistingBills] = useState([]);

 const COMPANY_STATE = "Maharashtra"; // Fallback default

 const getSelectedOfficeState = (officeId) => {
   const targetId = officeId || formData.office;
   const selectedOffice = offices.find((o) => (o._id || "").toString() === (targetId || "").toString());
   if (!selectedOffice) return "Maharashtra";
   let st = selectedOffice.state || "";
   if (!st && selectedOffice.address) {
     const addr = selectedOffice.address.toLowerCase();
     if (addr.includes("uttar pradesh") || addr.includes("noida")) st = "Uttar Pradesh";
     else if (addr.includes("maharashtra") || addr.includes("mumbai")) st = "Maharashtra";
   }
   return st || "Maharashtra";
 };

 const [formData, setFormData] = useState({
 expenseType: "",
    isRecurring: false,
    recurringFrequency: "Monthly",
    amount: "",
 expenseDate: new Date().toISOString().split("T")[0],
 paymentMode: "Cash",
 paidTo: "",
 paidToName: "",
 remark: "",
 office: "",
 cgst: 0,
 sgst: 0,
 igst: 0,
 cgstAmount: 0,
 sgstAmount: 0,
 igstAmount: 0,
 totalAmount: 0,
 addGst: false,
 });

 useEffect(() => {
 fetchInitialData();
 }, []);

 const fetchInitialData = async () => {
 try {
 setLoading(true);
 const requests = [
 api.get("/offices"),
 api.get("/expense-types?status=Active"),
 api.get("/vendors"),
 ];
 if (isEdit) {
 requests.push(api.get(`/expenses/${id}`));
 }

 const [officesRes, etRes, vRes, expRes] = await Promise.all(requests);

 if (officesRes.data.success) setOffices(officesRes.data.data);
 if (etRes.data.success) setExpenseTypes(etRes.data.data);
 if (vRes.data.success) setVendors(vRes.data.data);

 if (isEdit && expRes?.data.success) {
 const exp = expRes.data.data;
 const bills = [
 ...(exp.bill ? [exp.bill] : []),
 ...(Array.isArray(exp.bills) ? exp.bills : []),
 ].filter(Boolean);
 const dedupedBills = bills.filter((bill, index, self) => {
 const key = `${bill.filePath || ""}::${bill.fileName || ""}`;
 return index === self.findIndex((item) => `${item.filePath || ""}::${item.fileName || ""}` === key);
 });
 setExistingBills(dedupedBills);
 const isRec = Boolean(exp.isRecurring);
        setFormData({
          expenseType: exp.expenseType || "",
          isRecurring: isRec,
          recurringFrequency: exp.recurringFrequency || "Monthly",
          amount: exp.amount || "",
 expenseDate: exp.expenseDate ? new Date(exp.expenseDate).toISOString().split("T")[0] : "",
 paymentMode: exp.paymentMode || "Cash",
 paidTo: exp.paidTo || "",
 paidToName: exp.paidToName || exp.paidTo || "",
 remark: exp.remark || "",
 office: exp.office?._id || exp.office || "",
 cgst: exp.cgst || 0,
 sgst: exp.sgst || 0,
 igst: exp.igst || 0,
 cgstAmount: exp.cgstAmount || 0,
 sgstAmount: exp.sgstAmount || 0,
 igstAmount: exp.igstAmount || 0,
 totalAmount: exp.totalAmount || 0,
 addGst: exp.addGst || false,
 });
 }
 } catch (err) {
 console.error("Error fetching initial data:", err);
 swal("Error", "Failed to load required data", "error");
 } finally {
 setLoading(false);
 }
 };

 const applyGSTByState = (vendorState, officeId) => {
 let cgst = 0, sgst = 0, igst = 0;

 if (!vendorState) {
 igst = 18;
 return { cgst, sgst, igst };
 }

 const normalize = (s) => (s || "").toString().trim().toLowerCase();
 const vState = normalize(vendorState);
 const company = normalize(getSelectedOfficeState(officeId));

 if (vState === company) {
 cgst = 9;
 sgst = 9;
 } else {
 igst = 18;
 }

 return { cgst, sgst, igst };
 };

 useEffect(() => {
 const amount = parseFloat(formData.amount || 0);
 const cgstAmount = parseFloat(formData.cgstAmount || 0);
 const sgstAmount = parseFloat(formData.sgstAmount || 0);
 const igstAmount = parseFloat(formData.igstAmount || 0);

 const total = formData.addGst 
 ? amount + cgstAmount + sgstAmount + igstAmount 
 : amount;

 setFormData((prev) => ({
 ...prev,
 totalAmount: total,
 }));
 }, [formData.amount, formData.cgstAmount, formData.sgstAmount, formData.igstAmount, formData.addGst]);

 const handleInputChange = (e) => {
 const { name, value, type, checked } = e.target;
 
 if (name === "office") {
 const amount = parseFloat(formData.amount || 0);
 const selectedVendor = vendors.find(v => v._id === formData.paidTo || v.name === formData.paidTo);
 const vendorState = selectedVendor?.state || "";
 if (formData.addGst) {
 const { cgst, sgst, igst } = applyGSTByState(vendorState, value);
 setFormData(prev => ({
 ...prev,
 office: value,
 cgst,
 sgst,
 igst,
 cgstAmount: (amount * cgst) / 100,
 sgstAmount: (amount * sgst) / 100,
 igstAmount: (amount * igst) / 100,
 }));
 return;
 }
 setFormData(prev => ({ ...prev, office: value }));
 } else if (name === "paidTo") {
 const selectedVendor = vendors.find(v => v._id === value || v.name === value);
 const amount = parseFloat(formData.amount || 0);
 
 if (selectedVendor) {
 const vendorState = selectedVendor.state || "";
 const { cgst, sgst, igst } = applyGSTByState(vendorState, formData.office);
 setFormData(prev => ({
 ...prev,
 paidTo: selectedVendor.name,
 paidToName: selectedVendor.name,
 cgst: prev.addGst ? cgst : 0,
 sgst: prev.addGst ? sgst : 0,
 igst: prev.addGst ? igst : 0,
 cgstAmount: prev.addGst ? (amount * cgst) / 100 : 0,
 sgstAmount: prev.addGst ? (amount * sgst) / 100 : 0,
 igstAmount: prev.addGst ? (amount * igst) / 100 : 0,
 }));
 } else {
 const igst = formData.addGst ? 18 : 0;
 setFormData(prev => ({
 ...prev,
 paidTo: value,
 paidToName: value,
 cgst: 0,
 sgst: 0,
 igst: igst,
 cgstAmount: 0,
 sgstAmount: 0,
 igstAmount: (amount * igst) / 100,
 }));
 }
 } else if (name === "addGst") {
 const amount = parseFloat(formData.amount || 0);
 if (checked) {
 const selectedVendor = vendors.find(v => v._id === formData.paidTo || v.name === formData.paidTo);
 const vendorState = selectedVendor?.state || "";
 const { cgst, sgst, igst } = applyGSTByState(vendorState, formData.office);
 setFormData(prev => ({
 ...prev,
 addGst: true,
 cgst,
 sgst,
 igst,
 cgstAmount: (amount * cgst) / 100,
 sgstAmount: (amount * sgst) / 100,
 igstAmount: (amount * igst) / 100,
 }));
 } else {
 setFormData(prev => ({
 ...prev,
 addGst: false,
 cgst: 0,
 sgst: 0,
 igst: 0,
 cgstAmount: 0,
 sgstAmount: 0,
 igstAmount: 0,
 }));
 }
 } else if (name === "amount") {
 const amount = parseFloat(value || 0);
 setFormData(prev => ({
 ...prev,
 amount: value,
 cgstAmount: (amount * prev.cgst) / 100,
 sgstAmount: (amount * prev.sgst) / 100,
 igstAmount: (amount * prev.igst) / 100,
 }));
 } else if (["cgst", "sgst"].includes(name)) {
 const rate = parseFloat(value || 0);
 const amount = parseFloat(formData.amount || 0);
 const gstAmt = (amount * rate) / 100;
 setFormData(prev => ({
 ...prev,
 cgst: value,
 sgst: value,
 cgstAmount: gstAmt,
 sgstAmount: gstAmt,
 igst: 0,
 igstAmount: 0,
 }));
 } else if (name === "igst") {
 const rate = parseFloat(value || 0);
 const amount = parseFloat(formData.amount || 0);
 const gstAmt = (amount * rate) / 100;
 setFormData(prev => ({
 ...prev,
 igst: value,
 igstAmount: gstAmt,
 cgst: 0,
 cgstAmount: 0,
 sgst: 0,
 sgstAmount: 0,
 }));
 } else if (["cgstAmount", "sgstAmount"].includes(name)) {
 const gstAmt = parseFloat(value || 0);
 const amount = parseFloat(formData.amount || 0);
 const rate = amount > 0 ? ((gstAmt / amount) * 100).toFixed(2) : 0;
 setFormData(prev => ({
 ...prev,
 cgstAmount: value,
 sgstAmount: value,
 cgst: rate,
 sgst: rate,
 igst: 0,
 igstAmount: 0,
 }));
 } else if (name === "igstAmount") {
 const gstAmt = parseFloat(value || 0);
 const amount = parseFloat(formData.amount || 0);
 const rate = amount > 0 ? ((gstAmt / amount) * 100).toFixed(2) : 0;
 setFormData(prev => ({
 ...prev,
 igstAmount: value,
 igst: rate,
 cgst: 0,
 cgstAmount: 0,
 sgst: 0,
 sgstAmount: 0,
 }));
 } else if (name === "expenseType") {
      const selectedType = expenseTypes.find(t => (t.name || "").toLowerCase() === (value || "").toLowerCase());
      const isRecurring = selectedType?.category === "Recurring";
      setFormData(prev => ({
        ...prev,
        expenseType: value,
        isRecurring: isRecurring,
        recurringFrequency: isRecurring ? (prev.recurringFrequency || selectedType?.recurringFrequency || "Monthly") : "",
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: type === "checkbox" ? checked : value,
      }));
    }
 };

 const handleFileChange = (e) => {
 const incomingFiles = Array.from(e.target.files || []);
 setSelectedFiles((prev) => {
 const merged = [...prev, ...incomingFiles];
 const seen = new Set();
 return merged.filter((file) => {
 const key = `${file.name}::${file.size}::${file.lastModified}`;
 if (seen.has(key)) return false;
 seen.add(key);
 return true;
 });
 });
 e.target.value = "";
 };

 const handleSubmit = async (e) => {
 e.preventDefault();
 try {
 setSubmitting(true);
 const data = new FormData();
 Object.keys(formData).forEach((key) => {
 data.append(key, formData[key]);
 });
 selectedFiles.forEach((file) => {
 data.append("bill", file);
 });

 data.set("subTotal", formData.amount);
 data.set("totalAmount", formData.totalAmount);
 data.set("cgstAmount", formData.cgstAmount);
 data.set("sgstAmount", formData.sgstAmount);
 data.set("igstAmount", formData.igstAmount);
 data.set("gstAmount", formData.cgstAmount + formData.sgstAmount + formData.igstAmount);
 data.set("paidBy", "CANSCORP ENGINEERS PVT. LTD.");

 let res;
 if (isEdit) {
 res = await api.patch(`/expenses/${id}`, data, {
 headers: { "Content-Type": "multipart/form-data" },
 });
 } else {
 res = await api.post("/expenses", data, {
 headers: { "Content-Type": "multipart/form-data" },
 });
 }

 if (res.data.success) {
 swal("Success", isEdit ? "Office expense updated successfully" : "Office expense added successfully", "success");
 navigate(-1);
 }
 } catch (err) {
 swal("Error", err.response?.data?.message || "Failed to save office expense", "error");
 } finally {
 setSubmitting(false);
 }
 };

 const paymentModes = [
 "Cash", "Bank Transfer", "UPI", "Credit Card", "Cheque", "Other"
 ];

 if (loading) {
 return (
 <div className="d-flex justify-content-center align-items-center" style={{ height: "300px" }}>
 <Spinner animation="border" variant="primary" />
 </div>
 );
 }

 return (
 <Fragment>
 <PageTitle activeMenu={isEdit ? "Edit Office Expense" : "Add Office Expense"} motherMenu="Accounts" />

 <Row>
 <Col lg={12}>
 <Card>
 <Card.Header className="d-flex justify-content-between align-items-center">
 <Card.Title>{isEdit ? "Edit Office Expense" : "Add Office Expense"}</Card.Title>
 <Button 
 variant="secondary" 
 onClick={() => navigate(-1)}
 >
 Back
 </Button>
 </Card.Header>
 <Card.Body>
 <Form onSubmit={handleSubmit}>
 <Row>
 <Col md={formData.isRecurring ? 3 : 6} className="mb-3">
                  <Form.Group>
                    <Form.Label>Expense Type *</Form.Label>
                    <Form.Select
                      name="expenseType"
                      value={formData.expenseType}
                      onChange={handleInputChange}
                      required
                    >
                      <option value="">Select Type</option>
                      {expenseTypes.map((type) => (
                        <option key={type._id} value={type.name}>
                          {type.name}</option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>

                {formData.isRecurring && (
                  <Col md={3} className="mb-3">
                    <Form.Group>
                      <Form.Label className="d-flex align-items-center justify-content-between">
                        <span><i className="fa fa-sync-alt text-primary me-1"></i> Recurring Frequency *</span>
                        <span className="badge badge-sm bg-light text-primary font-weight-bold" style={{ fontSize: "10px" }}>Fixed/Editable</span>
                      </Form.Label>
                      <Form.Select
                        name="recurringFrequency"
                        value={formData.recurringFrequency || "Monthly"}
                        onChange={handleInputChange}
                        required
                      >
                        <option value="Daily">Daily</option>
                        <option value="Weekly">Weekly</option>
                        <option value="Monthly">Monthly</option>
                        <option value="Yearly">Yearly</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                )}
 <Col md={6} className="mb-3">
 <Form.Group>
 <Form.Label>Amount (Base) *</Form.Label>
 <Form.Control
 type="number"
 name="amount"
 placeholder="Enter base amount"
 value={formData.amount}
 onChange={handleInputChange}
 required
 />
 </Form.Group>
 </Col>
 <Col md={6} className="mb-3">
 <Form.Group>
 <Form.Label>Expense Date *</Form.Label>
 <Form.Control
 type="date"
 name="expenseDate"
 value={formData.expenseDate}
 onChange={handleInputChange}
 required
 />
 </Form.Group>
 </Col>
 <Col md={6} className="mb-3">
 <Form.Group>
 <Form.Label>Payment Mode *</Form.Label>
 <Form.Select
 name="paymentMode"
 value={formData.paymentMode}
 onChange={handleInputChange}
 required
 >
 <option value="">Select Payment Mode</option>
 {paymentModes.map(mode => (
 <option key={mode} value={mode}>{mode}</option>
 ))}
 </Form.Select>
 </Form.Group>
 </Col>
 <Col md={6} className="mb-3">
 <Form.Group>
 <Form.Label>Paid To *</Form.Label>
 <Form.Control
 list="vendor-list"
 name="paidTo"
 placeholder="Select vendor or type manually"
 value={formData.paidTo}
 onChange={handleInputChange}
 required
 />
 <datalist id="vendor-list">
 {vendors.map(v => (
 <option key={v._id} value={v.name} />
 ))}
 </datalist>
 </Form.Group>
 </Col>
 <Col md={6} className="mb-3">
 <Form.Group>
 <Form.Label>Office *</Form.Label>
 <Form.Select
 name="office"
 value={formData.office}
 onChange={handleInputChange}
 required
 >
 <option value="">Select Office</option>
 {offices.map(off => (
 <option key={off._id} value={off._id}>{off.officeName}</option>
 ))}
 </Form.Select>
 </Form.Group>
 </Col>
 <Col md={12} className="mb-3">
 <Form.Group>
 <Form.Label>Remark</Form.Label>
 <Form.Control
 as="textarea"
 rows={2}
 name="remark"
 placeholder="Enter remark..."
 value={formData.remark}
 onChange={handleInputChange}
 />
 </Form.Group>
 </Col>
 <Col md={6} className="mb-3">
 <Form.Group>
 <Form.Label>{isEdit ? "Update Bill" : "Upload Bill"}</Form.Label>
 <Form.Control
 type="file"
 multiple
 accept=".pdf,.jpg,.jpeg,.png,.gif,.doc,.docx,.xls,.xlsx,.csv,.zip,.rar,.7z"
 onChange={handleFileChange}
 />
 {isEdit && <Form.Text className="text-muted">Leave empty to keep existing bills. You can add files one by one.</Form.Text>}
 {existingBills.length > 0 && (
 <div className="mt-2 small">
 <div className="fw-semibold">Current bills</div>
 {existingBills.map((bill, index) => (
 <div key={`${bill.filePath || bill.fileName || index}`}>
 {bill.fileName || `Bill ${index + 1}`}
 </div>
 ))}
 </div>
 )}
 {selectedFiles.length > 0 && (
 <div className="mt-2 small">
 <div className="fw-semibold">New selection</div>
 {selectedFiles.map((file, index) => (
 <div key={`${file.name}-${index}`}>{file.name}</div>
 ))}
 </div>
 )}
 </Form.Group>
 </Col>
 <Col md={6} className="mb-3">
 <div className="d-flex align-items-center mt-4">
 <Form.Check
 type="checkbox"
 id="addGst"
 name="addGst"
 label="Add GST?"
 checked={formData.addGst}
 onChange={handleInputChange}
 />
 </div>
 </Col>

 {formData.addGst && (
 <Fragment>
 <Col md={4} className="mb-3">
 <Form.Group>
 <Form.Label>CGST %</Form.Label>
 <Form.Control
 type="number"
 name="cgst"
 value={formData.cgst}
 onChange={handleInputChange}
 />
 </Form.Group>
 </Col>
 <Col md={4} className="mb-3">
 <Form.Group>
 <Form.Label>SGST %</Form.Label>
 <Form.Control
 type="number"
 name="sgst"
 value={formData.sgst}
 onChange={handleInputChange}
 />
 </Form.Group>
 </Col>
 <Col md={4} className="mb-3">
 <Form.Group>
 <Form.Label>IGST %</Form.Label>
 <Form.Control
 type="number"
 name="igst"
 value={formData.igst}
 onChange={handleInputChange}
 />
 </Form.Group>
 </Col>
 <Col md={4} className="mb-3">
 <Form.Group>
 <Form.Label>CGST Amount</Form.Label>
 <Form.Control
 type="number"
 name="cgstAmount"
 value={formData.cgstAmount}
 onChange={handleInputChange}
 />
 </Form.Group>
 </Col>
 <Col md={4} className="mb-3">
 <Form.Group>
 <Form.Label>SGST Amount</Form.Label>
 <Form.Control
 type="number"
 name="sgstAmount"
 value={formData.sgstAmount}
 onChange={handleInputChange}
 />
 </Form.Group>
 </Col>
 <Col md={4} className="mb-3">
 <Form.Group>
 <Form.Label>IGST Amount</Form.Label>
 <Form.Control
 type="number"
 name="igstAmount"
 value={formData.igstAmount}
 onChange={handleInputChange}
 />
 </Form.Group>
 </Col>
 </Fragment>
 )}

 <Col md={12} className="mt-3">
 <div className="border rounded p-3">
 <Row>
 <Col md={6}>
 <h5>Summary</h5>
 <p className="mb-1">Base Amount: ₹ {parseFloat(formData.amount || 0).toLocaleString("en-IN")}</p>
 {formData.addGst && (
 <Fragment>
 <p className="mb-1">CGST: ₹ {parseFloat(formData.cgstAmount || 0).toLocaleString("en-IN")}</p>
 <p className="mb-1">SGST: ₹ {parseFloat(formData.sgstAmount || 0).toLocaleString("en-IN")}</p>
 <p className="mb-1">IGST: ₹ {parseFloat(formData.igstAmount || 0).toLocaleString("en-IN")}</p>
 </Fragment>
 )}
 <h4 className="mt-2 text-primary">Total Amount: ₹ {parseFloat(formData.totalAmount || 0).toLocaleString("en-IN")}</h4>
 </Col>
 </Row>
 </div>
 </Col>

 <Col md={12} className="mt-4">
 <Button variant="primary" type="submit" disabled={submitting}>
 {submitting ? (isEdit ? "Updating..." : "Adding...") : (isEdit ? "Update Expense" : "Add Expense")}
 </Button>
 </Col>
 </Row>
 </Form>
 </Card.Body>
 </Card>
 </Col>
 </Row>
 </Fragment>
 );
};

export default OfficeExpenseForm;
